// src/prototype/utils/opportunityRanking.ts
// ============================================================
// SHARED OPPORTUNITY RANKING
//
// Single source of truth for how opportunities are ranked, so the Use Case
// Explorer list and the freemium top-N detail gate always agree on rank.
//
// RANK BASIS = the regenerated composite (Option D root), identical to the
// expression EAOpportunitiesPage sorts by: featured opps read
// compositeForUseCase(OPP_TO_UC[id]); fillers fall back to their effort/TTV
// proxy capped below the featured band so they never outrank a featured use
// case. Off-strategy items are penalised below page 1.
//
// This previously ranked by the raw proxy alone, which diverged from the
// Explorer after the composite re-root (the proxy saturates at ROI ≥ $5M, so
// several fillers outranked featured use cases) — and with it the freemium
// top-3 gate. Both now read the same basis.
// ============================================================

import { MOCK_OPPORTUNITIES } from '../data/mockOpportunities';
import { calculateWeightedScore } from './weighting';
import { OPP_TO_UC, compositeForUseCase } from '../data/adapters/stage300FeaturedContent';

/** Fillers are capped below the featured composite band (mirrors the Explorer). */
export const FILLER_SCORE_CAP = 58;


// ============================================================
// INCLUSION FLOOR — a real rule, not a copy assertion
// ============================================================
// The Portfolio Strategy report used to say "N initiatives that scored above the inclusion
// threshold" while N was simply the fixture array's length: nothing was ever filtered, and two
// items scoring -942 were counted as having cleared a threshold. This constant makes the claim
// true by making it do something.
//
// THE VALUE. Ranked by the same composite the Explorer sorts on, every scored opportunity sits
// well above zero (the lowest is Comprehensive Virtual Care Ecosystem at ~11). The two off-strategy
// non-US items that once sat at -942 below this floor were removed from the demo on Oct 21, along
// with the other non-US use cases, so today the floor excludes nothing. It stays as a real rule:
// anything that scores below zero is not counted as having qualified.
//
// This selects from the SCORED set. It is deliberately not a filter over the 174-use-case
// library: 53 of the scored opportunities do not appear in that library at all, and the library
// carries no score column, so no threshold over it could ever reproduce this set. See BACKLOG.md.
export const INCLUSION_THRESHOLD = 0;

/** One scored opportunity, before the floor is applied. */
export interface ScoredOpportunity {
  id: string;
  score: number;
}

/** Every opportunity with its ranking score, highest first. No filtering. */
export function computeScoredOpportunities(categoryWeights: CategoryWeights): ScoredOpportunity[] {
  return MOCK_OPPORTUNITIES
    .map((opp) => {
      const composite = compositeForUseCase(OPP_TO_UC[opp.id]);
      const proxy = calculateWeightedScore(
        {
          roiEstimate: opp.roiEstimate,
          implementationEffort: opp.implementationEffort,
          timeToValue: opp.timeToValue,
        },
        categoryWeights,
      );
      const base = composite ?? Math.min(FILLER_SCORE_CAP, proxy);
      return {
        id: opp.id,
        score: base,
      };
    })
    .sort((a, b) => b.score - a.score);
}

/** The opportunities that clear the inclusion floor, highest first. THE derived set. */
export function computeIncludedOpportunities(categoryWeights: CategoryWeights): ScoredOpportunity[] {
  return computeScoredOpportunities(categoryWeights).filter((o) => o.score >= INCLUSION_THRESHOLD);
}

/** How many opportunities cleared the floor. Never hardcode this number in copy. */
export function computeIncludedCount(categoryWeights: CategoryWeights): number {
  return computeIncludedOpportunities(categoryWeights).length;
}

// Category weights map, typed off the scorer so it can never drift.
type CategoryWeights = Parameters<typeof calculateWeightedScore>[1];

/**
 * Ordered opportunity ids, ranked by composite (descending).
 * The same computation the Explorer uses to assign row rank.
 */
export function computeRankedOpportunityIds(categoryWeights: CategoryWeights): string[] {
  // Ranking still spans the FULL scored set — the floor governs what the report counts as
  // having qualified, not what the Explorer is allowed to list.
  return computeScoredOpportunities(categoryWeights).map((o) => o.id);
}

/** 1-based rank of an opportunity in the ranked list; 0 if not found. */
export function getOpportunityRank(id: string, categoryWeights: CategoryWeights): number {
  if (!id) return 0;
  const idx = computeRankedOpportunityIds(categoryWeights).indexOf(id);
  return idx < 0 ? 0 : idx + 1;
}
